import { forwardRef, useCallback } from 'react';
import { jsx, Button, Link, Heading, Input, Text } from 'theme-ui';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var buttonSizes = {
  sm: {
    fontSize: 1,
    height: "32px",
    px: 3
  },
  md: {
    fontSize: 2,
    height: "40px",
    px: 4
  },
  lg: {
    fontSize: 3,
    height: "48px",
    px: 6
  }
};

var getButtonSx = function getButtonSx(intent, appearance, size) {
  var sizes = buttonSizes[size];
  var appearanceSx;
  var hoverAppearanceSx;
  var disabledAppearanceSx;

  switch (appearance) {
    case "default":
      {
        appearanceSx = {
          backgroundColor: "".concat(intent, ".core"),
          borderColor: "".concat(intent, ".core"),
          color: "white"
        };
        hoverAppearanceSx = {
          backgroundColor: "".concat(intent, ".dark"),
          borderColor: "".concat(intent, ".dark")
        };
        disabledAppearanceSx = {
          backgroundColor: "gray.1",
          borderColor: "gray.1"
        };
        break;
      }

    case "outline":
      {
        appearanceSx = {
          backgroundColor: "transparent",
          borderColor: "".concat(intent, ".core"),
          color: "".concat(intent, ".core")
        };
        hoverAppearanceSx = {
          backgroundColor: "".concat(intent, ".lighter"),
          borderColor: "".concat(intent, ".dark"),
          color: "".concat(intent, ".dark")
        };
        disabledAppearanceSx = {
          borderColor: "gray.1",
          color: "gray.1"
        };
        break;
      }

    case "light":
      {
        appearanceSx = {
          backgroundColor: "white",
          borderColor: "white",
          color: "".concat(intent, ".core")
        };
        hoverAppearanceSx = {
          backgroundColor: "".concat(intent, ".lighter"),
          color: "".concat(intent, ".dark")
        };
        disabledAppearanceSx = {
          color: "gray.1"
        };
        break;
      }
  }

  return _extends({}, sizes, appearanceSx, {
    fontFamily: "body",
    lineHeight: "body",
    fontWeight: "body",
    borderRadius: "4px",
    paddingTop: 0,
    paddingBottom: 0,
    display: "inline-flex",
    alignItems: "center",
    textAlign: "center",
    textDecoration: "none",
    transition: "\n      color .15s ease-in-out,\n      background-color .15s ease-in-out,\n      border-color .15s ease-in-out\n    ",
    borderWidth: 1,
    borderStyle: "solid",
    whiteSpace: "nowrap",
    "&:not(:disabled)": {
      "&:hover": _extends({}, hoverAppearanceSx, {
        textDecoration: "none",
        cursor: "pointer",
        "&:active": {
          textDecoration: "none"
        }
      })
    },
    "&:disabled": _extends({}, disabledAppearanceSx, {
      cursor: "not-allowed"
    })
  });
};
/**
 * Basic button. See DUIButtonLink for more.
 */


var DUIButton = /*#__PURE__*/forwardRef(function DUIButton(_ref, ref) {
  var _ref$intent = _ref.intent,
      intent = _ref$intent === void 0 ? "primary" : _ref$intent,
      _ref$appearance = _ref.appearance,
      appearance = _ref$appearance === void 0 ? "default" : _ref$appearance,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? "md" : _ref$size,
      otherProps = _objectWithoutProperties(_ref, ["intent", "appearance", "size"]);

  return jsx(Button, _extends({
    ref: ref
  }, otherProps, {
    sx: getButtonSx(intent, appearance, size)
  }));
});
/**
 * Button for linking to external sites.
 */

var DUIButtonLink = /*#__PURE__*/forwardRef(function DUIButtonLink(_ref, ref) {
  var _ref$appearance = _ref.appearance,
      appearance = _ref$appearance === void 0 ? "default" : _ref$appearance,
      _ref$intent = _ref.intent,
      intent = _ref$intent === void 0 ? "primary" : _ref$intent,
      rel = _ref.rel,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? "md" : _ref$size,
      target = _ref.target,
      otherProps = _objectWithoutProperties(_ref, ["appearance", "intent", "rel", "size", "target"]);

  return jsx(Link, _extends({
    ref: ref,
    rel: rel || (target === "_blank" ? "noopener noreferrer" : undefined),
    target: target
  }, otherProps, {
    sx: getButtonSx(intent, appearance, size)
  }));
});
/**
 * Responsive font sizes at different breakpoints.
 */

var sizeSx = {
  xs: {
    fontSize: [0, 0]
  },
  sm: {
    fontSize: [0, 1]
  },
  md: {
    fontSize: [1, 2]
  },
  lg: {
    fontSize: [2, 3]
  },
  xl: {
    fontSize: [3, 4]
  },
  "2xl": {
    fontSize: [4, 5]
  },
  "3xl": {
    fontSize: [5, 6]
  }
};
var weightSx = {
  light: {
    fontWeight: "light"
  },
  regular: {
    fontWeight: "regular"
  },
  semibold: {
    fontWeight: "semibold"
  }
};

var getFontSizeSx = function getFontSizeSx(size) {
  return sizeSx[size];
};

var getFontWeightSx = function getFontWeightSx(weight) {
  return weightSx[weight];
};
/**
 * Responsive heading with configurable size and weight.
 */


var DUIHeading = /*#__PURE__*/forwardRef(function DUIHeading(_ref, ref) {
  var _ref$size = _ref.size,
      size = _ref$size === void 0 ? "xl" : _ref$size,
      otherProps = _objectWithoutProperties(_ref, ["size"]);

  return jsx(Heading, _extends({
    ref: ref
  }, otherProps, {
    sx: getFontSizeSx(size)
  }));
});
var inputSx = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "gray.1",
  height: "48px",
  paddingX: 4,
  borderRadius: "4px",
  fontSize: 3,
  backgroundColor: "white",
  "::placeholder": {
    color: "gray.2"
  }
};
/**
 * Basic input element.
 */

var DUIInput = /*#__PURE__*/forwardRef(function DUIInput(_ref, ref) {
  var onChange = _ref.onChange,
      onValueChange = _ref.onValueChange,
      otherProps = _objectWithoutProperties(_ref, ["onChange", "onValueChange"]);

  var handleChange = useCallback(function (event) {
    var value = event.currentTarget.value;

    if (onChange) {
      onChange(event);
    }

    if (onValueChange) {
      onValueChange(value);
    }
  }, [onChange, onValueChange]);
  return jsx(Input, _extends({
    ref: ref,
    onChange: handleChange
  }, otherProps, {
    sx: inputSx
  }));
});
/**
 * Responsive text with configurable size and weight.
 */

var DUIText = /*#__PURE__*/forwardRef(function DUIText(_ref, ref) {
  var size = _ref.size,
      weight = _ref.weight,
      otherProps = _objectWithoutProperties(_ref, ["size", "weight"]);

  return jsx(Text, _extends({
    ref: ref
  }, otherProps, {
    sx: _extends({}, size && getFontSizeSx(size), weight && getFontWeightSx(weight))
  }));
});
var baseColors = {
  primary: {
    dark: "#1034B8",
    core: "#254CDD",
    light: "#4C84FF",
    lighter: "#E8F4FD"
  },
  success: {
    dark: "#25B47E",
    core: "#46CD92",
    light: "#70E7B3",
    lighter: "#EBF4EC"
  },
  warning: {
    dark: "#DA972E",
    core: "#F0AD44",
    light: "#F1BE6E",
    lighter: "#FDF0E3"
  },
  danger: {
    dark: "#CD4F38",
    core: "#EB5D44",
    light: "#F38E7C",
    lighter: "#FAEAEA"
  },
  gray: ["#EAECF0", "#CACED4", "#A0A4AC", "#767B81", "#52555B", "#414348", "#212122"],
  black: "#000000",
  white: "#FFFFFF"
};
var theme = {
  colors: _extends({}, baseColors, {
    background: baseColors.white,
    text: baseColors.black
  }),
  space: [0, 4, 8, 12, 16, 20, 24, 28, 32, 36],
  fonts: {
    body: "'Rubik', Helvetica, Arial, sans-serif",
    heading: "'Rubik', Helvetica, Arial, sans-serif",
    monospace: "'Roboto Mono', monospace"
  },
  fontSizes: [10, 12, 14, 16, 18, 24, 32],
  fontWeights: {
    light: 300,
    regular: 400,
    semibold: 500,
    body: 400,
    heading: 400
  },
  lineHeights: {
    body: 1.5,
    heading: 1.125
  },
  text: {
    body: {
      fontFamily: "body",
      lineHeight: "body",
      fontWeight: "body"
    },
    heading: {
      fontFamily: "heading",
      lineHeight: "heading",
      fontWeight: "heading"
    }
  },
  breakpoints: ["600px", "960px", "1280px", "1920px"],
  // Mostly used for MDX. `root` style is applied to the <body> element.
  styles: {
    root: {
      fontFamily: "body",
      lineHeight: "body",
      fontWeight: "body",
      fontSize: getFontSizeSx("sm")
    },
    h1: {
      variant: "text.heading",
      fontSize: getFontSizeSx("3xl")
    },
    h2: {
      variant: "text.heading",
      fontSize: getFontSizeSx("2xl")
    },
    h3: {
      variant: "text.heading",
      fontSize: getFontSizeSx("xl")
    },
    h4: {
      variant: "text.heading",
      fontSize: getFontSizeSx("lg")
    },
    h5: {
      variant: "text.heading",
      fontSize: getFontSizeSx("md")
    },
    h6: {
      variant: "text.heading",
      fontSize: getFontSizeSx("sm")
    },
    p: {
      color: "text",
      fontFamily: "body",
      fontWeight: "body",
      lineHeight: "body"
    },
    a: {
      color: "primary"
    },
    pre: {
      fontFamily: "monospace",
      overflowX: "auto",
      code: {
        color: "inherit"
      }
    },
    code: {
      fontFamily: "monospace",
      fontSize: "inherit"
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      borderSpacing: 0
    },
    th: {
      textAlign: "left"
    },
    td: {
      textAlign: "left"
    },
    img: {
      maxWidth: "100%"
    }
  }
};
export { DUIButton, DUIButtonLink, DUIHeading, DUIInput, DUIText, theme };